import classNames                 from 'classnames';
import * as React                 from 'react';
import { useTranslation }         from 'react-i18next';
import ReactPaginate              from 'react-paginate';
import { useLocation, useParams } from 'react-router-dom';
import { SessionStorageService }  from 'ts-storage-service';

import { PAGE }    from '../../../../common/constants/pagesConstants';
import usePrevious from '../../../../common/hooks/usePrevious';

import CatalogCollapsible         from '../../../components/catalog/CatalogCollapsible';
import CatalogFilter              from '../../../components/catalog/CatalogFilter';
import CatalogGrid                from '../../../components/catalog/CatalogGrid';
import CompareCounter             from '../../../components/CompareCounter';
import Marquiz                    from '../../../components/Marquiz/Marquiz';
import Meta                       from '../../../components/meta';
import PageHeader                 from '../../../components/pageHeader';
import PaginationNextArrowIcon    from '../../../components/icons/PaginationNextArrowIcon';
import PaginationPreviosArrowIcon from '../../../components/icons/PaginationPreviosArrowIcon';
import Preloader                  from '../../../components/containers/preloader';
import { useCatalog }             from '../../../providers/catalog';
import { useCompare }             from '../../../providers/compare';
import { useUser }                from '../../../providers/user';

import { renderStaticSection } from '../../_common/utils/renderStaticSection';
import Notfound                from '../../notfound/components/Notfound';

import SortingSelect from './SortingSelect';

const Catalog = function() {
  const location = useLocation();
  const params   = useParams();
  const { t }    = useTranslation();

  const ss = new SessionStorageService();

  const catalog  = useCatalog();
  const compare  = useCompare();
  const prevPage = usePrevious(params.page);
  const user     = useUser();

  const { current: excludedPages } = React.useRef(new Set([
    PAGE.MEZHKOMNATNYE_DVERI,
  ]));
  const isSortSettingInProgressRef = React.useRef(false);

  const [currentPage, setCurrentPage] = React.useState(0);
  const [showSorting, setShowSorting] = React.useState(true);

  const initialStateLoaded = React.useMemo(() => {
    return catalog.catalog.initialStateLoaded && catalog.filter.initialStateLoaded;
  }, [catalog.catalog.initialStateLoaded, catalog.filter.initialStateLoaded]);
  const pageCount          = React.useMemo(() => {
    return Math.ceil(catalog.catalog.countModels / 24);
  }, [catalog.catalog.countModels]);
  const seoText            = React.useMemo(() => {
    try {
      return JSON.parse(catalog.catalog.meta.seoText);
    } catch {
      return {};
    }
  }, [catalog.catalog.meta.seoText]);

  const getInitialState = React.useCallback(() => {
    if (!catalog.filter.initialStateLoaded) {
      catalog.getCatalogFilterInitialState();
    }

    if (!catalog.catalog.initialStateLoaded) {
      catalog.getCatalogItems(`${location.pathname}${location.search}`);
    }
  }, [catalog.filter.initialStateLoaded, catalog.catalog.initialStateLoaded, location.pathname, location.search]);
  const onResize        = React.useCallback(() => {
    setShowSorting(window.screen.width >= 991);
  }, [showSorting]);
  const onPopState      = React.useCallback(() => {
    catalog.getCatalogItems(`${window.location.pathname}${window.location.search}`);
  }, []);
  const showMore        = React.useCallback((page) => {
    const offset = 24;

    catalog.showMoreItems(
      `${location.pathname}${location.search}${location.search.length > 0 ? '&' : '?'}count=${offset * page}`,
      catalog.catalog.sort,
    );
  }, [catalog.catalog.sort, location.pathname, location.search]);
  const handleClick     = React.useCallback((page) => {
    showMore(page);
    setCurrentPage(page);

    ss.setData('savedCatalogPage', page);

    window.scrollTo({
      behavior: 'auto',
      top:      0,
    });
  }, [showMore]);
  const setSortItems    = React.useCallback((dir) => {
    setCurrentPage(0);

    ss.setData('savedCatalogPage', 0);

    isSortSettingInProgressRef.current = true;

    catalog.setSort(`${location.pathname}${location.search}`, dir)
      .catch(() => {
        isSortSettingInProgressRef.current = false;
      });
  }, [location.pathname, location.search]);

  React.useEffect(() => {
    if (!initialStateLoaded && !catalog.loading) {
      getInitialState();
    }

    scrollTo({
      left: 0,
      top:  0,
    });

    onResize();

    window.addEventListener('popstate', onPopState);
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('popstate', onPopState);
    };
  }, []);
  React.useEffect(() => {
    if (window.screen.width >= 1152) {
      scrollTo({
        behavior: 'auto',
        left:     0,
        top:      0,
      });
    }
  }, [location.pathname]);
  React.useEffect(() => {
    if (isSortSettingInProgressRef.current) {
      const urlParams    = new URLSearchParams(location.search);
      const hasSortParam = urlParams.has('sort');

      if (hasSortParam) {
        isSortSettingInProgressRef.current = false;
      }
    }
  }, [location.search]);
  React.useEffect(() => {
    const savedCatalogPage = ss.getData('savedCatalogPage');

    setCurrentPage(savedCatalogPage ?? 0);

    if (savedCatalogPage) {
      handleClick(savedCatalogPage);
    } else if (prevPage !== params.page && !catalog.loading && !savedCatalogPage) {
      catalog.getCatalogItems(`${location.pathname}${location.search}`);
    }
  }, [params.page]);
  React.useEffect(() => {
    if (catalog.loading || isSortSettingInProgressRef.current) {
      return;
    }

    const urlParams    = new URLSearchParams(location.search);
    const hasSortParam = urlParams.has('sort');

    if (!hasSortParam && catalog.catalog.sort) {
      catalog.resetSort();
    }
  }, [params.page, location.search, catalog.catalog.sort, catalog.loading]);

  if (!initialStateLoaded && !catalog.loading) {
    return <Notfound />;
  }

  return (
    <>
      <div className={'page-content'}>
        <Meta {...catalog.catalog.meta} />
        <div className={'catalog_header_wrapper'}>
          <p className={classNames('found_doors', { hiddenFound: !catalog.filter.open })}>
            {`${t('client_found_doors')}: ${catalog.catalog.count}`}
          </p>
          <PageHeader>{catalog.catalog.meta.titleH1 || t('client_catalog')}</PageHeader>
          <section className={'top'}>
            {!excludedPages.has(params.page) && (
              <SortingSelect
                sort={catalog.catalog.sort}
                sortItemsFn={setSortItems}
              />
            )}
          </section>
        </div>
        <main id={'catalog'}>
          <div className={'top'}>
            <div className={'selects_wrapper'}>
              {!excludedPages.has(params.page) && (
                <SortingSelect
                  className={'sorting_select_mobile'}
                  sort={catalog.catalog.sort}
                  sortItemsFn={setSortItems}
                />
              )}
              <CatalogFilter
                resetPagination={() => {
                  ss.setData('savedCatalogPage', 0);

                  setCurrentPage(0);
                }}
                showSorting={!showSorting}
                sort={catalog.catalog.sort}
                sortFunc={setSortItems}
              />
            </div>
            <CatalogGrid
              count={catalog.catalog.count}
              countModels={catalog.catalog.countModels}
              initialStateLoaded={catalog.catalog.initialStateLoaded}
              installationTypes={catalog.catalog.installationTypes}
              isButtonsNextToSubtitle
              isInterior={catalog.catalog.isInterior}
              items={catalog.catalog.items}
              label={'client_found_doors'}
              meta={catalog.catalog.meta}
              modelCount={catalog.catalog.modelCount}
              popularModels={catalog.catalog.popularModels}
              schema
              shareButtonVisible
              showMore={catalog.catalog.showMore}
              showMoreFunc={showMore}
              showSorting={showSorting}
            />
          </div>
          <section className={'bottom'}>
            <span className={classNames('showMore', { show_more_hidden: !catalog.filter.open })}>
              {catalog.catalog.countModels > 24 && (
                <ReactPaginate
                  activeClassName={'active'}
                  breakClassName={'page_item'}
                  breakLabel={'...'}
                  containerClassName={'pagination'}
                  forcePage={currentPage}
                  marginPagesDisplayed={2}
                  nextLabel={currentPage < pageCount - 1 && <PaginationNextArrowIcon />}
                  onPageChange={(currentPage) => {
                    handleClick(currentPage.selected);
                  }}
                  pageClassName={'page_item'}
                  pageCount={pageCount}
                  pageRangeDisplayed={3}
                  previousLabel={currentPage > 0 && <PaginationPreviosArrowIcon />}
                  renderOnZeroPageCount={null}
                />
              )}
            </span>
          </section>
          <section className={'bottom'}>
            {seoText.hasOwnProperty('SECTIONS') && (
              <article>
                <CatalogCollapsible>
                  {seoText.SECTIONS.map((section, s) => {
                    return renderStaticSection(section, s);
                  })}
                </CatalogCollapsible>
              </article>
            )}
          </section>
          <CompareCounter isInterior={catalog.catalog.isInterior} />
        </main>
      </div>
      {(user.bookmarksLoading || catalog.loading) && <Preloader />}
      {!compare.tradeOffers.length && (
        <Marquiz id={catalog.catalog.isInterior ? '67d7f0f4b3dc1e0019149bd4' : '673c39c3c5d75c0026aed988'} />
      )}
    </>
  );
};

export default Catalog;